<template>
  <div class="col-12">
    <div class="lecture row">
      <div v-if="loading" class="col-12 mt-5">
        <div class="loading text-center pt-5">
          <div class="spinner-border" role="status">
            <span class="sr-only">Loading...</span>
          </div>
        </div>
      </div>
      <div v-else class="col-12 mt-5">
        <div v-if="lecture && lecture.error && lecture.errorCode === 404" class="error text-center">
          <h2>{{ lecture.errorText }}</h2>
          <p class="lead mb-5">{{ 'No lecture found' | translate }}</p>
          <button class="btn btn-primary" v-if="firstLecture" @click="goToFirstLecture">
            {{ 'Visit first lecture inside this course' | translate }}
          </button>
        </div>
        <div v-else-if="lecture && lecture.error && lecture.errorCode !== 401" class="error text-center">
          <h2>{{ lecture.errorText }}</h2>
        </div>
        <div v-else-if="lecture && lecture.error && lecture.errorCode === 401" class="mt-5 text-center bg-light p-5">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
            class="feather feather-lock"
          >
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          <div class="my-4">
            <h4 v-if="reason === 'Requires payment' || reason === 'Course requires payment'" class="">
              {{ 'This content is locked' | translate }}
            </h4>
            <h4 v-else-if="reason === 'Lecture requires login' || reason === 'Course requires login'" class="">
              {{ 'Sign up to see this content for free' | translate }}
            </h4>
            <h4 v-else-if="reason === 'This lecture is not available in chosen variant'" class="">
              {{ reason | translate }}
            </h4>
          </div>
          <div class="my-4" v-if="reason === 'Requires payment' || reason === 'Course requires payment'">
            <a v-if="course.is_free" class="btn btn-primary m-3" :href="`/${$route.params.course}`">
              {{ 'Enroll' | translate }}
            </a>
          </div>
          <div v-if="!isAuthenticated">
            <a v-if="course.is_free" class="btn btn-primary m-3" :href="`/${$route.params.course}`">
              {{ 'Enroll' | translate }}
            </a>
            <router-link :to="`/${$route.params.course}/watch/login`">
              {{ 'Sign in' | translate }}
            </router-link>
          </div>
        </div>

        <div v-if="lecture && lecture.components && lecture.components.length && !lecture.error" class="content">
          <div
            v-for="component in lecture.components"
            :key="component.id"
            class="row mb-4 d-flex justify-content-center"
          >
            <div :class="componentClasses(component.type)">
              <component
                v-bind:is="componentName(component.type)"
                :component="component"
                @videoEnded="videoEnded"
                @audioEnded="audioEnded"
                :ref="`component${component.id}`"
              ></component>
            </div>
          </div>
          <div class="row mb-4 d-flex justify-content-center" v-if="lecture">
            <div class="col-md-11 py-3 text-center">
              <CompleteAndContinue
                size="lg"
                class="btn btn-sm btn-dark"
                ref="completeAndContinue"
              ></CompleteAndContinue>
            </div>
          </div>
          <div class="row mb-4 d-flex justify-content-center" v-if="lecture && lecture.comments_module">
            <div class="col-md-11 pt-3 rounded">
              <Comments :commentable="lecture"></Comments>
            </div>
          </div>
        </div>
        <div v-else-if="lecture && !lecture.error">
          <div class="row mb-5">
            <div class="col-12 col-md-10 offset-md-1 col-lg-10 offset-lg-1">
              <div class="text-center py-5 bg-light">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  class="feather feather-video-off"
                >
                  <path
                    d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2m5.66 0H14a2 2 0 0 1 2 2v3.34l1 1L23 7v10"
                  />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
                <p class="lead mt-4">
                  {{ 'Oops... Nothing here at the moment' | translate }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12 d-lg-none">
        <div class="row">
          <div class="col-md-10 offset-md-1">
            <MobileTabNav></MobileTabNav>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import audioComponent from './../components/Audio'
import vimeoComponent from './../components/Vimeo'
import youtubeComponent from './../components/YouTube'
import videoComponent from './../components/Video'
import codeComponent from './../components/Code'
import quizComponent from './../components/Quiz'
import assignmentComponent from './../components/Assignment'
import htmlComponent from './../components/Html'
import imageComponent from './../components/Image'
import galleryComponent from './../components/Gallery'
import fileComponent from './../components/File'
import delayComponent from './../components/Delay'
import MobileTabNav from './../components/MobileTabNav'

import Comments from './../components/Comments'
import CompleteAndContinue from './../components/CompleteAndContinue'
import _ from 'lodash'

export default {
  components: {
    audioComponent,
    youtubeComponent,
    vimeoComponent,
    videoComponent,
    codeComponent,
    quizComponent,
    assignmentComponent,
    htmlComponent,
    imageComponent,
    galleryComponent,
    fileComponent,
    delayComponent,
    MobileTabNav,
    Comments,
    CompleteAndContinue,
  },
  data() {
    return {
      error: null,
      loading: false,
      lecture: {},
      reason: null,
    }
  },
  created() {
    this.handleRouteChange()
  },
  mounted() {},
  computed: {
    notSeenVideoComponents() {
      return this.$store.getters.notSeenVideoComponents
    },
    notListenedAudioComponents() {
      return this.$store.getters.notListenedAudioComponents
    },
    firstLecture() {
      return this.$store.getters.firstLecture
    },
    page() {
      return this.$store.state.page
    },
    utilityTab() {
      return this.$store.state.lectureUtilityTab
    },
    student() {
      return this.$store.state.user
    },
    isAuthenticated() {
      return this.$store.getters.isAuthenticated
    },
    course() {
      return this.$store.state.course
    },
    autoplay() {
      return this.$store.state.autoplay
    },
    autoplayAudio() {
      return this.$store.state.audioAutoplay
    },
  },
  watch: {
    $route: 'handleRouteChange',
  },
  methods: {
    componentClasses(type) {
      return _.includes(['video', 'image', 'html', 'gallery', 'vimeo', 'youtube'], type)
        ? `col-md-11 px-md-0`
        : `col-md-11 py-4`
    },
    audioEnded({ component_id }) {
      if (!this.autoplayAudio) {
        return null
      }

      let i = _.findIndex(this.lecture.components, (component) => component.id === component_id)
      this.$set(this.lecture.components, i, {
        ...this.lecture.components[i],
        listened: true,
      })

      this.autoPlayAudio()
    },
    videoEnded({ event, component_id }) {
      if (!this.autoplay) {
        return null
      }

      let i = _.findIndex(this.lecture.components, (component) => component.id === component_id)
      this.$set(this.lecture.components, i, {
        ...this.lecture.components[i],
        watched: true,
      })

      this.autoPlayVideo()
    },
    autoPlayAudio() {
      if (this.notListenedAudioComponents.length) {
        let refName = `component${this.notListenedAudioComponents[0].id}`
        let nextAudio = this.$refs[refName][0]
        this.scrollToElement(refName)
        nextAudio.play()
      } else {
        let completeButton = this.$refs.completeAndContinue
        if (completeButton) {
          completeButton.completeLecture()
        }
      }
    },
    autoPlayVideo() {
      if (this.notSeenVideoComponents.length) {
        let refName = `component${this.notSeenVideoComponents[0].id}`
        let nextVideo = this.$refs[refName][0]
        this.scrollToElement(refName)
        nextVideo.play()
      } else {
        let completeButton = this.$refs.completeAndContinue
        if (completeButton) {
          completeButton.completeLecture()
        }
      }
    },
    scrollToElement(refName) {
      let element = this.$refs[refName][0].$el
      let elementPosition = element.getBoundingClientRect().top
      let currentPosition = window.pageYOffset
      let step = (elementPosition - currentPosition) / 50
      let intervalId = setInterval(() => {
        currentPosition += step
        if (Math.abs(currentPosition - elementPosition) <= Math.abs(step)) {
          clearInterval(intervalId)
        }
        window.scrollTo(0, currentPosition)
      }, 10)
    },
    goToFirstLecture() {
      if (this.firstLecture) {
        return this.$router.push({
          name: 'lecture',
          params: { lecture: this.firstLecture.id },
        })
      }
      return null
    },
    componentName(type) {
      const component = {
        video: 'videoComponent',
        youtube: 'youtubeComponent',
        vimeo: 'vimeoComponent',
        text: 'htmlComponent',
        html: 'htmlComponent',
        file: 'fileComponent',
        audio: 'audioComponent',
        image: 'imageComponent',
        gallery: 'galleryComponent',
        quiz: 'quizComponent',
        delay: 'delayComponent',
        code: 'codeComponent',
        assignment: 'assignmentComponent',
      }

      return component[type]
    },
    handleRouteChange() {
      if (this.lecture && !this.lecture.components) {
        this.fetchData()
      }

      if (this.lecture && this.$route.params.lecture !== 'login') {
        try {
          localStorage.setItem(this.$route.params.course, this.$route.params.lecture)
        } catch (error) {}
      }
    },
    fetchUser() {
      this.$api.getStudent().then((response) => {
        this.$store.commit('login', response.data)
      })
    },
    scrollToTop() {
      let currentPosition = window.pageYOffset
      let step = currentPosition / 50
      let intervalId = setInterval(() => {
        currentPosition -= step
        if (currentPosition <= 0) {
          clearInterval(intervalId)
        }
        window.scrollTo(0, currentPosition)
      }, 10)
    },
    fetchData() {
      this.loading = true
      this.reason = null

      this.$store
        .dispatch('fetchLecture', this.$route.params.lecture)
        .then((response) => {
          this.loading = false
          let newLecture = {
            ...response.lecture,
            error: null,
            errorStatus: null,
            errorText: null,
          }
          this.lecture = newLecture
          this.$store.commit('currentLecture', newLecture)
          return true
        })
        .then(() => {
          if (this.autoplay && this.notSeenVideoComponents.length) {
            this.autoPlayVideo()
          } else if (this.autoplayAudio && this.notListenedAudioComponents.length) {
            this.autoPlayAudio()
          } else if (window.innerWidth < 768) {
            this.scrollToTop()
          }
        })
        .catch((error) => {
          if (error.response) {
            let errorText = ''
            let status = _.get(error, 'response.status')

            if (status === 401) {
              this.reason = _.get(error, 'response.data.message')
              if (this.reason === 'This lecture is not available in chosen variant') {
                errorText = this.$t(this.reason)
                iziToast.info({
                  title: this.$t('No access'),
                  message: errorText,
                })
              } else {
                errorText = this.$t('Purchase or log in to watch this lecture')
                iziToast.info({
                  title: this.$t('No access'),
                  message: errorText,
                })
              }
            } else if (status === 419) {
              this.$emit('loginModal')
              errorText = this.$t('Please reload the page')
              iziToast.info({
                title: this.$t('Reload'),
                message: errorText,
              })
            } else if (status === 404) {
              errorText = this.$t('404')
            } else {
              console.error(error)
            }

            this.lecture = {
              ...this.lecture,
              error: true,
              errorCode: status,
              errorText: errorText,
            }
            this.$store.commit('currentLecture', this.lecture)
          } else {
            console.error(error)
          }

          this.loading = false
        })
    },
  },
}
</script>
